import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON } from '@/lib/api'
import type { SegmentCondition } from '@/pages/SegmentRuleFields'
import { SegmentEventPicker, type SegmentEvent } from '@/pages/SegmentEventPicker'

export const eventConditionPhrases = {
  event_attendee: ['Is a registered attendee of', 'Is not a registered attendee of'],
  event_buyer: ['Bought places for', 'Has not bought places for'],
  event_checked_in: ['Checked in at', 'Did not check in at'],
} as const
export type EventConditionField = keyof typeof eventConditionPhrases
export interface SegmentEventField {
  field: EventConditionField
  label: string
  group: string
  operators: string[]
}

const segmentEventFieldsQuery = {
  queryKey: ['segment-fields'],
  queryFn: () => getJSON<{ fields: SegmentEventField[] }>('/api/v1/segments/fields'),
  retry: false,
}

/** The event conditions the API offers, for the builder's Add condition menu. */
export function useSegmentEventFields(): SegmentEventField[] {
  const fields = useQuery(segmentEventFieldsQuery)
  return fields.isError ? [] : (fields.data?.fields ?? [])
}

/** The Events an event condition can name, shared by the picker and the read-only rule. */
export function useSegmentEvents() {
  const events = useQuery({
    queryKey: ['segment-events'],
    queryFn: () => getJSON<{ events: SegmentEvent[] }>('/api/v1/events?segment_conditions=true'),
    retry: false,
  })
  return {
    events: events.isError ? [] : (events.data?.events ?? []),
    loading: events.isLoading,
    error: events.isError,
  }
}

export function SegmentEventConditions({
  conditions,
  onChange,
}: {
  conditions: SegmentCondition[]
  onChange: React.Dispatch<React.SetStateAction<SegmentCondition[]>>
}) {
  const fields = useQuery(segmentEventFieldsQuery)
  const events = useSegmentEvents()
  const replace = (index: number, next?: SegmentCondition) =>
    onChange((current) =>
      current.flatMap((condition, position) =>
        position === index ? (next ? [next] : []) : [condition],
      ),
    )
  return (
    <>
      {fields.isLoading && (
        <p className="text-caption text-muted-foreground">Loading event conditions…</p>
      )}
      {fields.isError && (
        <p role="alert" className="text-caption text-destructive">
          Event conditions unavailable
        </p>
      )}
      {conditions.map((condition, index) =>
        condition.field in eventConditionPhrases ? (
          <EventConditionRow
            key={index}
            condition={condition}
            events={events.events}
            loading={events.loading}
            error={events.error}
            onChange={(next) => replace(index, next)}
            onRemove={() => replace(index)}
          />
        ) : null,
      )}
    </>
  )
}

function EventConditionRow({
  condition,
  events,
  loading,
  error,
  onChange,
  onRemove,
}: {
  condition: SegmentCondition
  events: SegmentEvent[]
  loading: boolean
  error: boolean
  onChange: (condition: SegmentCondition) => void
  onRemove: () => void
}) {
  const phrases = eventConditionPhrases[condition.field as EventConditionField]
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 rounded-md border border-input px-3 py-2 sm:flex sm:flex-wrap sm:items-center">
      <Select
        value={condition.operator}
        onValueChange={(operator) => onChange({ ...condition, operator })}
      >
        <SelectTrigger aria-label="Event condition" className="w-full min-w-0 sm:w-72">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="is">{phrases[0]}</SelectItem>
          <SelectItem value="is_not">{phrases[1]}</SelectItem>
        </SelectContent>
      </Select>
      {condition.values.length > 1 && (
        <span className="col-span-2 text-body-sm text-muted-foreground">any of</span>
      )}
      <SegmentEventPicker
        events={events}
        selected={condition.values as number[]}
        loading={loading}
        error={error}
        onChange={(values) => onChange({ ...condition, values })}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="col-start-2 row-start-1 ml-auto text-muted-foreground"
        aria-label="Remove condition"
        onClick={onRemove}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  )
}
